/** Single party card — extracted from RichPartiesBody for CC-10. */
import { Building2, User, Mail, Phone, MapPin, FileSignature } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PartyVerificationBadge, type PartyVerification } from './PartyVerificationBadge';

const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

export function PartyCard({
  party,
  verification,
}: {
  party: Record<string, unknown>;
  verification?: PartyVerification;
}) {
  const sig = obj(party.signatory);
  const contact = obj(party.contact_details);
  const isIndividual = asText(party.entity_type) === 'Individual';
  const Icon = isIndividual ? User : Building2;
  return (
    <Card className="border-border">
      <CardContent className="pt-3 pb-3 px-4 space-y-2">
        <PartyVerificationBadge verification={verification} />
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {asText(party.legal_name) || '—'}
              </div>
              {asText(party.trading_name) && (
                <div className="text-xs text-muted-foreground">
                  d/b/a {asText(party.trading_name)}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {asText(party.party_id) && (
              <Badge variant="outline" className="text-[10px]">{asText(party.party_id)}</Badge>
            )}
            {asText(party.role) && (
              <Badge variant="outline" className="text-[10px]">{asText(party.role)}</Badge>
            )}
          </div>
        </div>

        {asText(party.obligations_summary) && (
          <p className="text-xs text-muted-foreground">{asText(party.obligations_summary)}</p>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
          {asText(party.entity_type) && (
            <div><span className="text-muted-foreground">Type: </span>{asText(party.entity_type)}</div>
          )}
          {asText(party.country_of_incorporation) && (
            <div><span className="text-muted-foreground">Country: </span>{asText(party.country_of_incorporation)}</div>
          )}
          {asText(party.registration_number) && (
            <div className="col-span-2"><span className="text-muted-foreground">Reg #: </span>{asText(party.registration_number)}</div>
          )}
          {asText(party.registered_address) && (
            <div className="col-span-2 flex items-start gap-1">
              <MapPin className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
              <span>{asText(party.registered_address)}</span>
            </div>
          )}
          {asText(party.liability_cap_mentioned) && (
            <div className="col-span-2 text-amber-700 dark:text-amber-400">
              Liability cap: {asText(party.liability_cap_mentioned)}
            </div>
          )}
        </div>

        {(asText(sig.name) || asText(sig.title)) && (
          <div className="rounded border bg-muted/30 px-2 py-1 text-xs">
            <div className="flex items-center gap-1 text-muted-foreground mb-0.5">
              <FileSignature className="h-3 w-3" aria-hidden="true" />
              Signatory
            </div>
            <div>
              {asText(sig.name)}{asText(sig.title) ? ` · ${asText(sig.title)}` : ''}
            </div>
            {asText(sig.signing_capacity) && (
              <div className="text-muted-foreground">{asText(sig.signing_capacity)}</div>
            )}
          </div>
        )}

        {(asText(contact.email) || asText(contact.phone) || asText(contact.address_for_notices)) && (
          <div className="text-xs space-y-0.5">
            {asText(contact.email) && (
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                <span>{asText(contact.email)}</span>
              </div>
            )}
            {asText(contact.phone) && (
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                <span>{asText(contact.phone)}</span>
              </div>
            )}
            {asText(contact.address_for_notices) && (
              <div className="text-muted-foreground">
                Notices: {asText(contact.address_for_notices)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
